import { useCallback, useEffect, useState } from "react";

export type DispatchToken = (token: string | null) => void;

interface IUseRegisterSWHook {
  registerLoading: boolean;
  dispatchToken: DispatchToken;
}

export function useRegisterSWHook(path: string, token: string): IUseRegisterSWHook {
  const [registerLoading, setRegisterLoading] = useState<boolean>(false);
  const [worker, setWorker] = useState<ServiceWorker>();

  const register = useCallback(async (): Promise<void> => {
    try {
      let registerRes: ServiceWorker | null = null;
      setRegisterLoading(true);
      if (!("serviceWorker" in navigator)) {
        throw new Error("Service Worker is not available");
      }
      const registration: ServiceWorkerRegistration = await navigator.serviceWorker.register(
        `${path}`
      );
      console.log(`Registration [${path}] succeeded. Scope is ` + registration.scope);

      if (registration.installing) {
        setWorker(registration.installing);
        registerRes = registration.installing;
      } else if (registration.waiting) {
        setWorker(registration.waiting);
        registerRes = registration.waiting;
      } else if (registration.active) {
        setWorker(registration.active);
        registerRes = registration.active;
      }
      registerRes?.postMessage({ token: `Bearer ${token}` || "" });
      console.log("[Registration finished]");
    } catch (error) {
      if (import.meta.env.VITE_NODE_ENV === "development") {
        return;
      }
      setTimeout(register, 1000 * 30);
      console.log(`Registration [${path}] failed with ` + error);
    } finally {
      setRegisterLoading(false);
    }
  }, [navigator, path]);

  useEffect(() => {
    register();
  }, []);

  useEffect(() => {
    dispatchToken(token);
  }, [token]);

  const dispatchToken = useCallback(
    (token: string | null): void => {
      if (worker) {
        worker.postMessage({ token: `Bearer ${token}` || "" });
      }
    },
    [worker]
  );

  return {
    dispatchToken,
    registerLoading
  };
}
